'use client';

import {
  AcceptOwnershipTransferRequestBody,
  isProfessionalVictimRelationship,
} from 'bff';
import { VictimRelationshipTypes } from 'database';
import { useTranslations } from 'i11n';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'ui';
import { nativeEnumToArray } from 'utils';

/**
 * What the incoming claimant declares when taking a claim over: the
 * relationship they hold with the victim and, when they act for them
 * professionally, the CUIT the claim has to be filed under.
 */
export const ClaimTransferAcceptanceFields = ({
  form,
}: {
  form: UseFormReturn<AcceptOwnershipTransferRequestBody>;
}) => {
  const t = useTranslations();

  const relationshipOptions = useMemo(
    () =>
      nativeEnumToArray(VictimRelationshipTypes).map((relationship) => ({
        value: relationship,
        label: t(`victim_relationship.${relationship}`),
      })),
    [t],
  );

  const relationship = form.watch('victim_relationship');

  return (
    <div className='space-y-4'>
      <FormField
        control={form.control}
        name='victim_relationship'
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t('claim.attributes.claimants.victim_relationship.label')}
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={t(
                      'claim_ownership_transfer.victim_relationship_placeholder',
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {relationshipOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {relationship === 'other' && (
        <FormField
          control={form.control}
          name='victim_relationship_other'
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(
                  'claim.attributes.claimants.victim_relationship_other.label',
                )}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value || ''}
                  placeholder={t(
                    'claim_ownership_transfer.victim_relationship_other_placeholder',
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {isProfessionalVictimRelationship(relationship) && (
        <FormField
          control={form.control}
          name='identification'
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('claim_ownership_transfer.identification_label')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value || ''}
                  inputMode='numeric'
                  placeholder={t(
                    'claim_ownership_transfer.identification_placeholder',
                  )}
                />
              </FormControl>
              <FormDescription>
                {t('claim_ownership_transfer.identification_hint')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
};
